import React, { useState, useEffect, useCallback } from "react";
import authFetch from "../../authFetch";

/* ══ PERSONAL HA TOKEN MANAGER ════════════════════════════
   Workspace users can optionally add their own HA Long-Lived
   Access Token alongside the workspace-shared token. Personal
   tokens take priority when present.

   Benefits:
   - HA history shows the real user who performed actions
   - Users with different HA roles (admin/viewer) can be
     represented correctly
   - Revocation is per-user, not workspace-wide                */

function SmartHomePersonalToken({ locId, locLabel, currentUser, isRTL, isOwner }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newToken, setNewToken] = useState("");
  const [scope, setScope] = useState("personal");
  const [saving, setSaving] = useState(false);

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`/api/ha-credentials?loc_id=${encodeURIComponent(locId)}&list=1`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load tokens");
      }
      const body = await res.json();
      setTokens(body.tokens || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [locId]);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  async function saveToken() {
    if (!newToken.trim()) {
      setError(isRTL ? "أدخل رمز الوصول" : "Please enter an access token.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await authFetch("/api/ha-credentials", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locId, token: newToken.trim(), scope }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to save");
      setNewToken("");
      await loadTokens();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeToken(tokenScope) {
    if (!window.confirm(
      isRTL
        ? `حذف الرمز ${tokenScope === "workspace" ? "المشترك" : "الشخصي"}؟`
        : `Delete the ${tokenScope === "workspace" ? "workspace" : "personal"} token?`
    )) return;

    setError("");
    try {
      const res = await authFetch(`/api/ha-credentials?loc_id=${encodeURIComponent(locId)}&scope=${tokenScope}`, {
        method: "DELETE",
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || "Failed to delete");
      await loadTokens();
    } catch (err) {
      setError(err.message);
    }
  }

  const personalToken = tokens.find(t => t.scope === "personal" && t.is_self);
  const workspaceToken = tokens.find(t => t.scope === "workspace");
  const otherPersonalTokens = tokens.filter(t => t.scope === "personal" && !t.is_self);

  function fmtDate(d) {
    if (!d) return "";
    return new Date(d).toLocaleDateString(isRTL ? "ar-SA" : "en-GB", { dateStyle: "medium" });
  }

  return (
    <div className="sh-pt">
      <h3 className="sh-section-title">{isRTL ? "رموز الوصول" : "Access Tokens"}</h3>
      <p className="sh-notif-desc">
        {isRTL
          ? "الرمز الشخصي يظهر نشاطك في سجل Home Assistant باسمك. إذا لم يوجد، يُستخدم الرمز المشترك."
          : "Your personal token makes actions in HA history attributable to you. Without one, the workspace-shared token is used."}
      </p>

      {error && <div className="sh-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading ? (
        <p style={{ color: "var(--g400)", padding: "1rem 0", textAlign: "center" }}>
          {isRTL ? "جارٍ التحميل..." : "Loading..."}
        </p>
      ) : (
        <>
          {/* Workspace-shared token */}
          <div className="sh-pt-card">
            <div className="sh-pt-header">
              <span className="sh-pt-badge sh-pt-badge-shared">{isRTL ? "مشترك" : "Workspace"}</span>
              <span className="sh-pt-title">
                {workspaceToken
                  ? (isRTL ? `تم التحديث ${fmtDate(workspaceToken.updated_at)}` : `Updated ${fmtDate(workspaceToken.updated_at)}`)
                  : (isRTL ? "غير مُعد" : "Not set")}
              </span>
              {workspaceToken && isOwner && (
                <button className="sh-lock-btn sh-lock-warn" onClick={() => removeToken("workspace")}>
                  {isRTL ? "حذف" : "Remove"}
                </button>
              )}
            </div>
            <p className="sh-pt-desc">
              {isRTL
                ? "الرمز الاحتياطي لجميع مستخدمي مساحة العمل."
                : "Fallback token used by any workspace user who has no personal token."}
            </p>
          </div>

          {/* Personal token */}
          <div className="sh-pt-card sh-pt-card-personal">
            <div className="sh-pt-header">
              <span className="sh-pt-badge sh-pt-badge-personal">{isRTL ? "شخصي" : "Personal"}</span>
              <span className="sh-pt-title">
                {personalToken
                  ? (isRTL ? `تم التحديث ${fmtDate(personalToken.updated_at)}` : `Updated ${fmtDate(personalToken.updated_at)}`)
                  : (isRTL ? "غير مُعد" : "Not set")}
              </span>
              {personalToken && (
                <button className="sh-lock-btn sh-lock-warn" onClick={() => removeToken("personal")}>
                  {isRTL ? "حذف" : "Remove"}
                </button>
              )}
            </div>
            <p className="sh-pt-desc">
              {isRTL
                ? `الرمز الخاص بـ ${currentUser?.name || "المستخدم"}. يُستخدم بدلاً من الرمز المشترك عند وجوده.`
                : `Your personal token (${currentUser?.name || "you"}). Takes priority over the workspace token when present.`}
            </p>
          </div>

          {/* Other personal tokens (owner view only) */}
          {isOwner && otherPersonalTokens.length > 0 && (
            <div className="sh-pt-others">
              <h4 className="sh-pt-others-title">
                {isRTL ? "الرموز الشخصية للآخرين" : "Other Users' Personal Tokens"}
              </h4>
              <div className="sh-pt-others-list">
                {otherPersonalTokens.map(t => (
                  <div key={t.user_id} className="sh-pt-other">
                    <span className="sh-pt-other-id">{t.user_id}</span>
                    <span className="sh-pt-other-date">{fmtDate(t.updated_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new token */}
          <div className="sh-pt-add">
            <h4 className="sh-pt-add-title">
              {isRTL ? "إضافة رمز جديد" : "Add Access Token"}
            </h4>
            <div className="sh-pt-scope">
              <label className="sh-pt-scope-opt">
                <input type="radio" name="token-scope" value="personal" checked={scope === "personal"} onChange={() => setScope("personal")} />
                <span>{isRTL ? "شخصي (أنا فقط)" : "Personal (me only)"}</span>
              </label>
              {isOwner && (
                <label className="sh-pt-scope-opt">
                  <input type="radio" name="token-scope" value="workspace" checked={scope === "workspace"} onChange={() => setScope("workspace")} />
                  <span>{isRTL ? "مشترك (مساحة العمل)" : "Workspace (shared)"}</span>
                </label>
              )}
            </div>
            <input
              className="fi"
              type="password"
              placeholder={isRTL ? "الصق رمز HA طويل الأمد" : "Paste HA Long-Lived Access Token"}
              value={newToken}
              onChange={e => setNewToken(e.target.value)}
              dir="ltr"
            />
            <p className="sh-hint">
              {isRTL
                ? "أنشئ من HA: الملف الشخصي → رموز الوصول طويلة الأمد"
                : "Create in HA: Profile → Long-Lived Access Tokens"}
            </p>
            <button className="btn btn-p" disabled={saving || !newToken.trim()} onClick={saveToken}>
              {saving
                ? (isRTL ? "جارٍ الحفظ..." : "Saving...")
                : (isRTL ? "حفظ الرمز" : "Save Token")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default SmartHomePersonalToken;
